import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import BaseAlert from 'react-bootstrap/Alert';
import divWithClassName from 'react-bootstrap/divWithClassName';
import { FormattedMessage } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import Hyperlink from '../Hyperlink';
import Button from '../Button';
import breakpoints from '../utils/breakpoints';
import { Icon } from '../KeyComponents';

export const ALERT_CLOSE_LABEL_TEXT = 'Dismiss';

const Alert = React.forwardRef(
  (
    {
      children,
      icon,
      iconSize,
      actions,
      dismissible,
      onClose,
      closeLabel,
      stacked,
      ...props
    },
    ref
  ) => {
    const [isStacked, setIsStacked] = useState(stacked);
    const isExtraSmall = useMediaQuery({ maxWidth: breakpoints.extraSmall.maxWidth });
    const actionButtonSize = 'sm';

    useEffect(() => {
      if (isExtraSmall) {
        setIsStacked(true);
      } else {
        setIsStacked(stacked);
      }
    }, [isExtraSmall, stacked]);

    const isOutlined = props.variant?.includes('outline');

    const cloneActionElement = useCallback((item, index) => (
      <span key={index}>
        <Hyperlink
          href={item.href}
          className={classNames('text-inherit mr-1', index > 0 ? 'ml-1' : 'ml-0 underlined')}
          target={item.target}
          onClick={item.onClick}
        >
          {item.label}
        </Hyperlink>
        {index === 0 && actions.length > 1 && (
          <span className={classNames('mx-1', isOutlined ? 'text-muted' : 'text-inherit')}>•</span>
        )}
      </span>
    ), [actions, isOutlined]);

    const CloseButton = useCallback(() => {
      if (closeLabel) {
        return (
          <Button
            size={actionButtonSize}
            variant="link"
            className="fs-3 text-white underlined"
            onClick={onClose}
          >
            <FormattedMessage
              id="pgn.Alert.closeLabel"
              defaultMessage="Dismiss"
              description="Label of a close button on Alert component"
            />
          </Button>
        );
      }
      return (
        <i
          className={classNames('ri-close-fill fs-2 align-self-start', { 'ml-auto': isStacked })}
          onClick={onClose}
          role="button"
          tabIndex={0}
          onKeyPress={onClose}
          aria-label="Close alert"
        />
      );
    }, [closeLabel, onClose, isStacked]);

    return (
      <BaseAlert
        {...props}
        show={isStacked ? true : props.show}
        className={classNames(
          'alert-content',
          isStacked && 'stacked',
          props.show && 'enter',
          (dismissible || actions?.length > 0) && 'alert-dismissible',
          props.className
        )}
        ref={ref}
      >
        <Icon icon={icon} type="fill" size={iconSize} className={classNames('alert-icon', { 'stacked align-self-start': isStacked })} />
        <div
          className={classNames({
            'pgn__alert-message-wrapper': !isStacked,
            'pgn__alert-message-wrapper-stacked': isStacked,
          })}
        >
          <div className={classNames({ 'mb-1 stacked': isStacked }, 'alert-message-content')}>
            {children}
          </div>
          {isStacked && (
            <div className={classNames('text-wrap', isOutlined ? 'text-muted' : 'text-inherit')}>
              {props.description}
            </div>
          )}
          {(dismissible || actions?.length > 0) && (
            <div className={classNames('pgn__alert-actions')}>
              {actions && actions.map(cloneActionElement)}
              {!isStacked && dismissible && <CloseButton />}
            </div>
          )}
        </div>
        {isStacked && dismissible && <CloseButton />}
      </BaseAlert>
    );
  }
);

const DivStyledAsH4 = divWithClassName('h4');
DivStyledAsH4.displayName = 'DivStyledAsH4';

function AlertHeading(props) {
  return <BaseAlert.Heading {...props} />;
}
function AlertLink(props) {
  return <BaseAlert.Link {...props} />;
}

const commonPropTypes = {
  as: PropTypes.elementType,
  bsPrefix: PropTypes.string,
};

AlertLink.propTypes = commonPropTypes;
AlertHeading.propTypes = commonPropTypes;

AlertLink.defaultProps = {
  as: 'a',
  bsPrefix: 'alert-link',
};

AlertHeading.defaultProps = {
  as: DivStyledAsH4,
  bsPrefix: 'alert-heading',
};

Alert.propTypes = {
  ...BaseAlert.propTypes,
  className: PropTypes.string,
  bsPrefix: PropTypes.string,
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'info',
    'dark',
    'light',
  ]),
  transition: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      in: PropTypes.bool,
      appear: PropTypes.bool,
      children: PropTypes.node,
      onEnter: PropTypes.func,
      onEntered: PropTypes.func,
      onEntering: PropTypes.func,
      onExit: PropTypes.func,
      onExited: PropTypes.func,
      onExiting: PropTypes.func,
    }),
  ]),
  children: PropTypes.node,
  icon: PropTypes.func,
  iconSize: PropTypes.string,
  show: PropTypes.bool,
  dismissible: PropTypes.bool,
  onClose: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.element),
  stacked: PropTypes.bool,
  closeLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

Alert.defaultProps = {
  ...BaseAlert.defaultProps,
  children: undefined,
  icon: undefined,
  iconSize: 'md',
  actions: undefined,
  dismissible: false,
  onClose: () => { },
  closeLabel: undefined,
  show: true,
  stacked: false,
};

Alert.Heading = AlertHeading;
Alert.Link = AlertLink;

export default Alert;
